import { debugLog } from '../utils/debugLog';

interface NavigatorConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  addEventListener?: (type: 'change', listener: () => void) => void;
  removeEventListener?: (type: 'change', listener: () => void) => void;
}

const getNavigatorConnection = (): NavigatorConnection | null => {
  const navigatorWithConnection = navigator as Navigator & { connection?: NavigatorConnection };
  return navigatorWithConnection.connection ?? null;
};

/**
 * Checkout Error Handler Service
 * 
 * Comprehensive error handling service for Shopify checkout failures and network issues.
 * Provides retry mechanisms, user-friendly error messages, and graceful degradation.
 * 
 * Features:
 * - Network connectivity detection
 * - Retry mechanisms with exponential backoff
 * - User-friendly error message mapping
 * - Timeout handling
 * - Error categorization and logging
 * - Recovery suggestions
 */

// Error categories for better handling
export enum CheckoutErrorCategory {
  NETWORK = 'network',
  CART = 'cart',
  PAYMENT = 'payment',
  SHOPIFY_API = 'shopify_api',
  VALIDATION = 'validation',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown'
}

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// Enhanced error interface
export interface CheckoutError {
  category: CheckoutErrorCategory;
  severity: ErrorSeverity;
  code: string;
  message: string;
  userMessage: string;
  recoverable: boolean;
  retryable: boolean;
  suggestions: string[];
  originalError?: Error;
  timestamp: Date;
  context?: Record<string, any>;
}

// Retry configuration
interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  retryableErrors: CheckoutErrorCategory[];
}

// Network status interface
interface NetworkStatus {
  online: boolean;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
}

/**
 * Checkout Error Handler Service Class
 */
class CheckoutErrorHandlerService {
  private retryConfig: RetryConfig;
  private networkStatus: NetworkStatus;
  private networkListeners: {
    online: () => void;
    offline: () => void;
    connectionChange?: () => void;
  } = {
    online: () => {},
    offline: () => {},
  };
  private errorLog: CheckoutError[] = [];

  constructor() {
    this.retryConfig = {
      maxAttempts: 3,
      baseDelay: 1000, // 1 second
      maxDelay: 10000, // 10 seconds
      backoffMultiplier: 2,
      retryableErrors: [
        CheckoutErrorCategory.NETWORK,
        CheckoutErrorCategory.TIMEOUT,
        CheckoutErrorCategory.SHOPIFY_API
      ]
    };

    // Default to online to avoid false negatives in environments where navigator.onLine is unreliable
    this.networkStatus = {
      online: true
    };

    this.initializeNetworkMonitoring();
  }

  /**
   * Initialize network monitoring with proper cleanup tracking
   */
  private initializeNetworkMonitoring(): void {
    // Update initial network status
    this.updateNetworkStatus();

    // Initialize network listeners
    this.networkListeners = {
      online: () => {
        this.updateNetworkStatus();
        debugLog('[CheckoutErrorHandler] Network connection restored');
      },
      offline: () => {
        this.updateNetworkStatus();
        debugLog('[CheckoutErrorHandler] Network connection lost');
      },
    };

    // Basic online/offline detection
    window.addEventListener('online', this.networkListeners.online);
    window.addEventListener('offline', this.networkListeners.offline);

    // Enhanced network information if available
    const connection = getNavigatorConnection();
    if (connection?.addEventListener) {
      this.updateNetworkStatus();

      this.networkListeners.connectionChange = () => {
        this.updateNetworkStatus();
        debugLog('[CheckoutErrorHandler] Network conditions changed:', this.networkStatus);
      };

      connection.addEventListener('change', this.networkListeners.connectionChange);
    }
  }

  /**
   * Cleanup network monitoring listeners
   */
  public cleanup(): void {
    if (this.networkListeners) {
      window.removeEventListener('online', this.networkListeners.online);
      window.removeEventListener('offline', this.networkListeners.offline);

      if (this.networkListeners.connectionChange) {
        const connection = getNavigatorConnection();
        connection?.removeEventListener?.('change', this.networkListeners.connectionChange);
      }
    }
  }

  /**
   * Update network status from navigator
   */
  private updateNetworkStatus(): void {
    // Fail open: assume online to avoid blocking checkout due to flaky navigator state
    this.networkStatus.online = true;
    
    const connection = getNavigatorConnection();
    if (connection) {
      this.networkStatus.effectiveType = connection.effectiveType;
      this.networkStatus.downlink = connection.downlink;
      this.networkStatus.rtt = connection.rtt;
    }
  }

  /**
   * Process and categorize an error
   */
  processError(error: Error | string, context?: Record<string, any>): CheckoutError {
    const errorMessage = error instanceof Error ? error.message : error;
    const originalError = error instanceof Error ? error : undefined;

    // Categorize the error
    const category = this.categorizeError(errorMessage);
    const severity = this.determineSeverity(category, errorMessage);
    const code = this.generateErrorCode(category, errorMessage);
    
    const checkoutError: CheckoutError = {
      category,
      severity,
      code,
      message: errorMessage,
      userMessage: this.generateUserMessage(category, errorMessage),
      recoverable: this.isRecoverable(category, errorMessage),
      retryable: this.isRetryable(category),
      suggestions: this.generateSuggestions(category, errorMessage),
      originalError,
      timestamp: new Date(),
      context
    };

    // Log the error
    this.logError(checkoutError);

    return checkoutError;
  }

  /**
   * Categorize error based on message content
   */
  private categorizeError(message: string): CheckoutErrorCategory {
    const lowerMessage = message.toLowerCase();

    // Network-related errors
    if (lowerMessage.includes('network') || 
        lowerMessage.includes('fetch') ||
        lowerMessage.includes('connection') ||
        lowerMessage.includes('offline') ||
        lowerMessage.includes('no internet')) {
      return CheckoutErrorCategory.NETWORK;
    }

    // Cart-related errors
    if (lowerMessage.includes('cart') ||
        lowerMessage.includes('empty') ||
        lowerMessage.includes('no items') ||
        lowerMessage.includes('inventory') ||
        lowerMessage.includes('stock')) {
      return CheckoutErrorCategory.CART;
    }

    // Payment-related errors
    if (lowerMessage.includes('payment') ||
        lowerMessage.includes('checkout') ||
        lowerMessage.includes('billing') ||
        lowerMessage.includes('card') ||
        lowerMessage.includes('declined')) {
      return CheckoutErrorCategory.PAYMENT;
    }

    // Shopify API errors
    if (lowerMessage.includes('shopify') ||
        lowerMessage.includes('graphql') ||
        lowerMessage.includes('api') ||
        lowerMessage.includes('user errors') ||
        lowerMessage.includes('storefront')) {
      return CheckoutErrorCategory.SHOPIFY_API;
    }

    // Validation errors
    if (lowerMessage.includes('validation') ||
        lowerMessage.includes('invalid') ||
        lowerMessage.includes('required') ||
        lowerMessage.includes('format')) {
      return CheckoutErrorCategory.VALIDATION;
    }

    // Timeout errors
    if (lowerMessage.includes('timeout') ||
        lowerMessage.includes('timed out') ||
        lowerMessage.includes('request took too long')) {
      return CheckoutErrorCategory.TIMEOUT;
    }

    return CheckoutErrorCategory.UNKNOWN;
  }

  /**
   * Determine error severity
   */
  private determineSeverity(category: CheckoutErrorCategory, message: string): ErrorSeverity {
    switch (category) {
      case CheckoutErrorCategory.NETWORK:
        return !this.networkStatus.online ? ErrorSeverity.HIGH : ErrorSeverity.MEDIUM;
      
      case CheckoutErrorCategory.PAYMENT:
        return ErrorSeverity.HIGH;
      
      case CheckoutErrorCategory.CART:
        return message.toLowerCase().includes('empty') ? ErrorSeverity.LOW : ErrorSeverity.MEDIUM;
      
      case CheckoutErrorCategory.SHOPIFY_API:
        return ErrorSeverity.MEDIUM;
      
      case CheckoutErrorCategory.VALIDATION:
        return ErrorSeverity.LOW;
      
      case CheckoutErrorCategory.TIMEOUT:
        return ErrorSeverity.MEDIUM;
      
      default:
        return ErrorSeverity.MEDIUM;
    }
  }

  /**
   * Generate error code
   */
  private generateErrorCode(category: CheckoutErrorCategory, message: string): string {
    const categoryCode = category.toUpperCase();
    const hash = this.simpleHash(message);
    return `CHECKOUT_${categoryCode}_${hash}`;
  }

  /**
   * Generate user-friendly error message
   */
  private generateUserMessage(category: CheckoutErrorCategory, message: string): string {
    switch (category) {
      case CheckoutErrorCategory.NETWORK:
        return 'There was a network issue contacting our store. Please try again.';
      
      case CheckoutErrorCategory.CART:
        if (message.toLowerCase().includes('empty')) {
          return 'Your cart is empty. Please add items before checking out.';
        }
        if (message.toLowerCase().includes('inventory') || message.toLowerCase().includes('stock')) {
          return 'Some items in your cart are no longer available. Please review your cart.';
        }
        return 'There was an issue with your cart. Please refresh and try again.';
      
      case CheckoutErrorCategory.PAYMENT:
        return 'There was an issue processing your payment. Please try again or use a different payment method.';
      
      case CheckoutErrorCategory.SHOPIFY_API:
        return 'Our store is temporarily experiencing issues. Please try again in a few moments.';
      
      case CheckoutErrorCategory.VALIDATION:
        return 'Please check that all required information is filled out correctly.';
      
      case CheckoutErrorCategory.TIMEOUT:
        return 'The request took too long to complete. Please try again.';
      
      default:
        return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
    }
  }

  /**
   * Check if error is recoverable
   */
  private isRecoverable(category: CheckoutErrorCategory, message: string): boolean {
    switch (category) {
      case CheckoutErrorCategory.NETWORK:
        return true; // Network issues are usually temporary
      
      case CheckoutErrorCategory.CART:
        return !message.toLowerCase().includes('empty'); // Empty cart requires user action
      
      case CheckoutErrorCategory.PAYMENT:
        return true; // User can try different payment method
      
      case CheckoutErrorCategory.SHOPIFY_API:
        return true; // API issues are usually temporary
      
      case CheckoutErrorCategory.VALIDATION:
        return true; // User can fix validation issues
      
      case CheckoutErrorCategory.TIMEOUT:
        return true; // Timeouts can be retried
      
      default:
        return false;
    }
  }

  /**
   * Check if error is retryable
   */
  private isRetryable(category: CheckoutErrorCategory): boolean {
    return this.retryConfig.retryableErrors.includes(category);
  }

  /**
   * Generate recovery suggestions
   */
  private generateSuggestions(category: CheckoutErrorCategory, message: string): string[] {
    const suggestions: string[] = [];

    switch (category) {
      case CheckoutErrorCategory.NETWORK:
        suggestions.push('Check your internet connection');
        suggestions.push('Try refreshing the page');
        break;
      
      case CheckoutErrorCategory.CART:
        if (message.toLowerCase().includes('empty')) {
          suggestions.push('Add items to your cart');
        } else {
          suggestions.push('Refresh your cart');
          suggestions.push('Remove and re-add items');
        }
        break;
      
      case CheckoutErrorCategory.PAYMENT:
        suggestions.push('Try a different payment method');
        suggestions.push('Check your payment details');
        suggestions.push('Contact your bank if using a card');
        break;
      
      case CheckoutErrorCategory.SHOPIFY_API:
        suggestions.push('Wait a few moments and try again');
        suggestions.push('Refresh the page');
        break;
      
      case CheckoutErrorCategory.VALIDATION:
        suggestions.push('Check all required fields are filled');
        suggestions.push('Verify your information is correct');
        break;
      
      case CheckoutErrorCategory.TIMEOUT:
        suggestions.push('Try again with a stable connection');
        suggestions.push('Wait a moment before retrying');
        break;
      
      default:
        suggestions.push('Try refreshing the page');
        suggestions.push('Contact support if the issue persists');
    }

    return suggestions;
  }

  /**
   * Execute function with retry logic
   */
  async executeWithRetry<T>(
    operation: () => Promise<T>,
    context?: Record<string, any>
  ): Promise<T> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= this.retryConfig.maxAttempts; attempt++) {
      try {
        // Check network status before attempting
        if (!this.networkStatus.online) {
          throw new Error('No internet connection available');
        }

        // Add timeout wrapper
        const result = await this.withTimeout(operation(), 30000); // 30 second timeout
        
        // Success - log if this was a retry
        if (attempt > 1) {
          debugLog(`[CheckoutErrorHandler] Operation succeeded on attempt ${attempt}`);
        }
        
        return result;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        const checkoutError = this.processError(lastError, {
          ...context,
          attempt,
          maxAttempts: this.retryConfig.maxAttempts
        });

        // Don't retry if not retryable or on last attempt
        if (!checkoutError.retryable || attempt === this.retryConfig.maxAttempts) {
          throw lastError;
        }

        // Calculate delay with exponential backoff
        const delay = Math.min(
          this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, attempt - 1),
          this.retryConfig.maxDelay
        );

        debugLog(`[CheckoutErrorHandler] Attempt ${attempt} failed, retrying in ${delay}ms:`, checkoutError.userMessage);
        
        // Wait before retry
        await this.delay(delay);
      }
    }

    throw lastError;
  }

  /**
   * Wrap operation with timeout
   */
  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`Operation timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
  }

  /**
   * Delay utility
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => {
      const timeoutId = setTimeout(() => {
        clearTimeout(timeoutId);
        resolve();
      }, ms);
    });
  }

  /**
   * Log error for debugging and analytics
   */
  private logError(error: CheckoutError): void {
    // Add to error log (keep last 50 errors)
    this.errorLog.push(error);
    if (this.errorLog.length > 50) {
      this.errorLog.shift();
    }

    // Console logging with appropriate level
    const logLevel = error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.HIGH 
      ? 'error' : 'warn';
    
    console[logLevel](`[CheckoutErrorHandler] ${error.code}:`, {
      category: error.category,
      severity: error.severity,
      message: error.message,
      userMessage: error.userMessage,
      suggestions: error.suggestions,
      context: error.context
    });

    // Optional: Send to analytics service
    this.trackError(error);
  }

  /**
   * Track error for analytics
   */
  private trackError(error: CheckoutError): void {
    try {
      // Log for development
      if (process.env.NODE_ENV === 'development') {
        debugLog(`[CheckoutErrorHandler] Tracking error:`, error.code);
      }

      // Optional: Send to external error tracking service
      // Example: Sentry, LogRocket, etc.
    } catch (trackingError) {
      debugLog('[CheckoutErrorHandler] Failed to track error:', trackingError);
    }
  }

  /**
   * Simple hash function for error codes
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16).substring(0, 6);
  }

  /**
   * Get network status
   */
  getNetworkStatus(): NetworkStatus {
    return { ...this.networkStatus };
  }

  /**
   * Get error log
   */
  getErrorLog(): CheckoutError[] {
    return [...this.errorLog];
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }

  /**
   * Update retry configuration
   */
  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }

  /**
   * Get retry configuration
   */
  getRetryConfig(): RetryConfig {
    return { ...this.retryConfig };
  }
}

// Create and export singleton instance
export const checkoutErrorHandler = new CheckoutErrorHandlerService();

export default checkoutErrorHandler;
